// src/lib/sanitizeHtml.ts
export type SanitizeOptions = {
  // For DOMPurify fallback
  allowedTags?: string[]
  allowedAttrs?: string[]
}

let dompurifyPromise: Promise<(html: string, opts: SanitizeOptions) => string> | null = null

function hasNativeHtmlSanitizer(): boolean {
  return typeof window !== 'undefined' && 'Sanitizer' in window && 'setHTML' in Element.prototype
}

function nativeSanitizeToString(html: string): string {
  // Sanitizer API sanitizes *while inserting*, so we insert into a detached node and read back.
  const container = document.createElement('div')
  // Per MDN, setHTML takes an options object with `sanitizer`.  [oai_citation:1‡MDN Web Docs](https://developer.mozilla.org/en-US/docs/Web/API/Element/setHTML?utm_source=chatgpt.com)
  // Using the default config: new Sanitizer()  [oai_citation:2‡MDN Web Docs](https://developer.mozilla.org/en-US/docs/Web/API/Sanitizer)
  // @ts-expect-error Sanitizer is a new browser feature that we are polyfilling here
  container.setHTML(html, { sanitizer: new Sanitizer() })
  return container.innerHTML
}

async function getDompurifySanitizer(): Promise<(html: string, opts: SanitizeOptions) => string> {
  if (!dompurifyPromise) {
    dompurifyPromise = import('dompurify').then(({ default: DOMPurify }) => {
      return (html: string, opts: SanitizeOptions): string =>
        DOMPurify.sanitize(html, {
          // Keep this strict; expand only if you must.
          ALLOWED_TAGS: opts.allowedTags ?? ['pre', 'code', 'span', 'br'],
          ALLOWED_ATTR: opts.allowedAttrs ?? ['class'],
        }) as string
    })
  }
  return dompurifyPromise
}

export async function sanitizeHtml(html: string, opts: SanitizeOptions = {}): Promise<string> {
  if (hasNativeHtmlSanitizer()) {
    return nativeSanitizeToString(html)
  }
  const sanitizeWithDompurify = await getDompurifySanitizer()
  return sanitizeWithDompurify(html, opts)
}
